import React from 'react';
import { Header, Segment, Divider, Message, Grid, Icon, Popup, Label } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
    Radar, RadarChart, PolarGrid,
    PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import { UserRatingResponse, UserSportStats } from '../../app/models/UserRatingModels';
import { sportOptions } from '../../app/common/options/sportOptions';
import { socialSkillOptions } from '../../app/common/options/socialSkillOptions';
import { sportSkillOptions } from '../../app/common/options/sportSkillOptions';

interface UserStatsTabProps {
    userRatingData: UserRatingResponse | null;
    sportId?: string;
    isMobile: boolean;
}

function SelfAssessedLevelRow({
    level,
    sportName,
    isMobile,
    t
}: {
    level: number | null;
    sportName: string;
    isMobile: boolean;
    t: Function;
}) {
    return (
        <Segment style={{
            padding: isMobile ? '10px' : '12px 16px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            marginBottom: 8
        }}>
            {/* Header row: badge + label + info */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: level != null ? 8 : 0,
                flexWrap: 'wrap'
            }}>
                {/* "Self" badge */}
                <Label
                    size='mini'
                    style={{
                        background: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #fcd34d',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                    }}
                >
                    <Icon name='user' style={{ margin: 0 }} />
                    {t('userRatings.selfBadge')}
                </Label>

                <span style={{
                    fontSize: isMobile ? '0.82em' : '0.88em',
                    color: '#78350f',
                    fontWeight: 600,
                }}>
                    {t('userRatings.selfAssessedLevel', { sport: sportName })}
                </span>

                <Popup
                    trigger={
                        <Icon
                            name='info circle'
                            style={{ color: '#f59e0b', cursor: 'pointer' }}
                        />
                    }
                    content={t('userRatings.selfAssessedInfo')}
                    size='mini'
                    inverted
                />
            </div>

            {/* Stars row */}
            {level != null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map(star => (
                            <Icon
                                key={star}
                                name={star <= level ? 'star' : 'star outline'}
                                style={{
                                    color: star <= level ? '#f59e0b' : '#d1d5db',
                                    fontSize: isMobile ? '1em' : '1.15em',
                                    margin: 0
                                }}
                            />
                        ))}
                    </div>
                    <span style={{ fontSize: '0.82em', color: '#92400e', fontWeight: 700 }}>
                        {level}/5
                    </span>
                    <span style={{ fontSize: '0.78em', color: '#b45309' }}>
                        — {t(`favoriteSports.level.${getLevelKey(level)}`)}
                    </span>
                </div>
            ) : (
                <span style={{ fontSize: '0.82em', color: '#bbb', fontStyle: 'italic' }}>
                    {t('favoriteSports.notAssessed')}
                </span>
            )}
        </Segment>
    );
}

/** Map star count → level key */
function getLevelKey(level: number): string {
    const map: Record<number, string> = {
        1: 'beginner',
        2: 'casual',
        3: 'intermediate',
        4: 'advanced',
        5: 'competitive'
    };
    return map[level] ?? 'beginner';
}

/** Small banner shown above peer-rating charts */
function PeerRatingsBanner({ isMobile, t }: { isMobile: boolean; t: Function }) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
            padding: '6px 10px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 6,
            flexWrap: 'wrap'
        }}>
            <Label
                size='mini'
                style={{
                    background: '#dbeafe',
                    color: '#1e40af',
                    border: '1px solid #93c5fd',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                }}
            >
                <Icon name='users' style={{ margin: 0 }} />
                {t('userRatings.peerBadge')}
            </Label>
            <span style={{
                fontSize: isMobile ? '0.80em' : '0.85em',
                color: '#1e3a8a',
            }}>
                {t('userRatings.peerRatingsInfo')}
            </span>
        </div>
    );
}

export default observer(function UserStatsTab({
    userRatingData,
    sportId,
    isMobile
}: UserStatsTabProps) {
    const { t } = useTranslation();

    if (!userRatingData) {
        return (
            <Message warning>
                <Message.Header>{t('userRatings.noStats')}</Message.Header>
                <p>{t('userRatings.noStatsMessage')}</p>
            </Message>
        );
    }

    const getSportName = (id: string) => {
        const sport = sportOptions.find(s => s.value === id);
        if (!sport) return 'Unknown Sport';
        return t(`sports.${sport.label.toLowerCase()}`, { defaultValue: sport.label });
    };

    const getSocialSkillTranslationKey = (skillId: string) => {
        const skill = socialSkillOptions.find(s => s.value === skillId);
        if (!skill) return null;
        const labelToKey: { [key: string]: string } = {
            'Fair Play': 'fairplay', 'On Time': 'ontime', 'Chill': 'chill',
            'Friendly': 'friendly', 'Teamwork': 'teamwork'
        };
        return labelToKey[skill.label] || null;
    };

    const getSportSkillTranslationKey = (skillId: string, sportId: string) => {
        const skills = sportSkillOptions[sportId];
        if (!skills) return null;
        const skill = skills.find(s => s.value === skillId);
        if (!skill) return null;
        const labelToKey: { [key: string]: string } = {
            'Speed': 'speed', 'Dribbling': 'dribbling', 'Shooting': 'shooting',
            'Defense': 'defense', 'Strength': 'strength', 'Passing': 'passing',
            'Ball Control': 'ballcontrol', 'Positioning': 'positioning',
            'Stamina': 'stamina', 'Consistency': 'consistency', 'Pace': 'pace',
            'Endurance': 'endurance', 'Posture': 'posture', 'Awareness': 'awareness',
            'Tacking/Jibing': 'tackingjibing', 'Climbing': 'climbing',
            'Accuracy': 'accuracy', 'Style': 'style', 'Parallel Turns': 'parallelturns',
            'Game Sense': 'gamesense', 'Discipline': 'discipline', 'Volley': 'volley',
            'Throwing Accuracy': 'throwingaccuracy', 'Tackling': 'tackling',
            'Reaction Speed': 'reactionspeed', 'Fielding': 'fielding',
            'Expression': 'expression', 'Precision': 'precision', 'Spiking': 'spiking',
            'Maneuvering': 'maneuvering', 'Route Reading': 'routereading',
            'Stick Handling': 'stickhandling', 'Technique': 'technique',
            'Spatial Awareness': 'spatialawareness', 'Pole Planting': 'poleplanting',
            'Flexibility': 'flexibility', 'Puck Handling': 'puckhandling',
            'Teamwork': 'teamwork', 'Confidence': 'confidence',
            'Grip Control': 'gripcontrol', 'Power': 'power', 'Carving': 'carving',
            'Hitting': 'hitting', 'Serve': 'serve', 'Edge Control': 'edgecontrol',
            'Breathing': 'breathing', 'Navigation': 'navigation',
            'Boat Handling': 'boathandling', 'Efficiency': 'efficiency',
            'Spare Shooting': 'spareshooting', 'Rhythm': 'rhythm', 'Timing': 'timing',
            'Control': 'control', 'Paddling': 'paddling', 'Coordination': 'coordination',
            'Teamplay': 'teamplay', 'Board Control': 'boardcontrol',
            'Team Strategy': 'teamstrategy', 'Wave Reading': 'wavereading',
            'Blade Control': 'bladecontrol', 'Spin Control': 'spincontrol',
            'Water Reading': 'waterreading', 'Turning': 'turning', 'Focus': 'focus',
            'Batting': 'batting', 'Striking': 'striking', 'Dodging': 'dodging',
            'Finishing': 'finishing', 'Skating': 'skating', 'Smash': 'smash',
            'Field Awareness': 'fieldawareness', 'Jumping': 'jumping',
            'Core Strength': 'corestrength', 'Speed Control': 'speedcontrol',
            'Breathing Control': 'breathingcontrol', 'Form': 'form',
            'Paddling Technique': 'paddlingtechnique', 'Reflexes': 'reflexes',
            'Strategy': 'strategy', 'Checking': 'checking', 'Wind Reading': 'windreading',
            'Net Play': 'netplay', 'Footwork': 'footwork', 'Reaction Time': 'reactiontime',
            'Blocking': 'blocking', 'Cradling': 'cradling', 'Agility': 'agility',
            'Trick Execution': 'trickexecution', 'Communication': 'communication',
            'Preparedness': 'preparedness', 'Serving': 'serving', 'Cutting': 'cutting',
            'Balance': 'balance', 'Forehand': 'forehand', 'Bowling': 'bowling',
            'Pace Control': 'pacecontrol', 'Memorization': 'memorization',
            'Putting': 'putting', 'Commitment': 'commitment', 'Stability': 'stability',
            'Punching': 'punching', 'Adaptability': 'adaptability',
            'Swing Control': 'swingcontrol', 'Throwing': 'throwing',
            'Sail Trim': 'sailtrim', 'Grappling': 'grappling',
            'Synchronization': 'synchronization', 'Fluidity': 'fluidity',
            'Shot Accuracy': 'shotaccuracy', 'Backhand': 'backhand',
            'Placement': 'placement', 'Ground Balls': 'groundballs',
            'Catching': 'catching'
        };
        return labelToKey[skill.label] || null;
    };

    const renderSocialSkillsChart = () => {
        if (!userRatingData.userStats?.socialSkills ||
            userRatingData.userStats.socialSkills.length === 0) return null;

        const data = userRatingData.userStats.socialSkills
            .slice()
            .sort((a, b) => a.sortIndex - b.sortIndex)
            .map(skill => {
                const translationKey = getSocialSkillTranslationKey(skill.socialSkillId);
                return {
                    skill: translationKey ? t(`socialSkills.${translationKey}`) : skill.name,
                    value: skill.averageValue,
                    fullMark: 5
                };
            });

        return (
            <Segment style={{ padding: isMobile ? '10px' : undefined }}>
                <Header as={isMobile ? 'h4' : 'h3'} color='teal' dividing>
                    {t('userRatings.socialSkills')}
                </Header>
                <PeerRatingsBanner isMobile={isMobile} t={t} />
                <ResponsiveContainer width='100%' height={isMobile ? 250 : 300}>
                    <RadarChart data={data}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey='skill' tick={{ fontSize: isMobile ? 9 : 12 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} />
                        <Radar name='Rating' dataKey='value'
                            stroke='#00b5ad' fill='#00b5ad' fillOpacity={0.6} />
                    </RadarChart>
                </ResponsiveContainer>
            </Segment>
        );
    };

    const renderSportSkillChart = (sportStats: UserSportStats) => {
        if (!sportStats.sportSkills || sportStats.sportSkills.length === 0) return null;

        const data = sportStats.sportSkills
            .slice()
            .sort((a, b) => a.sortIndex - b.sortIndex)
            .map(skill => {
                const translationKey = getSportSkillTranslationKey(
                    skill.sportSkillId, sportStats.sportId
                );
                return {
                    skill: translationKey ? t(`sportSkills.${translationKey}`) : skill.name,
                    value: skill.averageValue,
                    fullMark: 5
                };
            });

        const localizedSportName = t(
            `sports.${sportStats.sportName.toLowerCase()}`,
            { defaultValue: sportStats.sportName }
        );

        const level = userRatingData.selfReportedLevels?.[sportStats.sportId] ?? null;

        return (
            <Segment key={sportStats.sportId} style={{ padding: isMobile ? '10px' : undefined }}>
                <Header as={isMobile ? 'h4' : 'h3'} color='blue' dividing>
                    {t('userRatings.sportSkills', { sport: localizedSportName })}
                    <Header.Subheader style={{ fontSize: isMobile ? '0.85rem' : undefined }}>
                        {t('userRatings.gamesPlayed', { count: sportStats.gamesPlayed })}
                    </Header.Subheader>
                </Header>

                {/* Self-assessed level */}
                <SelfAssessedLevelRow
                    level={level}
                    sportName={localizedSportName}
                    isMobile={isMobile}
                    t={t}
                />

                {/* Peer ratings chart */}
                <PeerRatingsBanner isMobile={isMobile} t={t} />

                <ResponsiveContainer width='100%' height={isMobile ? 250 : 300}>
                    <RadarChart data={data}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey='skill' tick={{ fontSize: isMobile ? 9 : 12 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} />
                        <Radar name='Rating' dataKey='value'
                            stroke='#2185d0' fill='#2185d0' fillOpacity={0.6} />
                    </RadarChart>
                </ResponsiveContainer>
            </Segment>
        );
    };

    // ── Sport-specific view ───────────────────────────────────────────────────
    if (sportId && userRatingData.userStats) {
        const sportSkills = userRatingData.userStats.sportSkills?.filter(
            s => s.sportId === sportId
        ) ?? [];

        const chartData = sportSkills
            .slice()
            .sort((a, b) => a.sortIndex - b.sortIndex)
            .map(skill => {
                const translationKey = getSportSkillTranslationKey(skill.sportSkillId, sportId);
                return {
                    skill: translationKey ? t(`sportSkills.${translationKey}`) : skill.name,
                    value: skill.averageValue,
                    fullMark: 5
                };
            });

        return (
            <div>
                <SelfAssessedLevelRow
                    level={userRatingData.selfReportedLevel ?? null}
                    sportName={getSportName(sportId)}
                    isMobile={isMobile}
                    t={t}
                />

                {renderSocialSkillsChart()}

                {sportSkills.length > 0 ? (
                    <Segment style={{ padding: isMobile ? '10px' : undefined }}>
                        <Header as={isMobile ? 'h4' : 'h3'} color='blue' dividing>
                            {t('userRatings.sportSkills', { sport: getSportName(sportId) })}
                        </Header>
                        <PeerRatingsBanner isMobile={isMobile} t={t} />
                        <ResponsiveContainer width='100%' height={isMobile ? 250 : 300}>
                            <RadarChart data={chartData}>
                                <PolarGrid />
                                <PolarAngleAxis
                                    dataKey='skill'
                                    tick={{ fontSize: isMobile ? 9 : 12 }}
                                />
                                <PolarRadiusAxis angle={90} domain={[0, 5]} />
                                <Radar name='Rating' dataKey='value'
                                    stroke='#2185d0' fill='#2185d0' fillOpacity={0.6} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </Segment>
                ) : (
                    <Message info>
                        <Message.Header>{t('userRatings.noSportStats')}</Message.Header>
                        <p>{t('userRatings.noSportStatsMessage', {
                            sport: getSportName(sportId)
                        })}</p>
                    </Message>
                )}
            </div>
        );
    }

    // ── All sports view ───────────────────────────────────────────────────────
    return (
        <div>
            {renderSocialSkillsChart()}

            <Divider section />

            {userRatingData.allSportStats && userRatingData.allSportStats.length > 0 ? (
                <div>
                    <Header as={isMobile ? 'h3' : 'h2'} color='blue'>
                        {t('userRatings.allSports')}
                    </Header>
                    {isMobile ? (
                        userRatingData.allSportStats.map(s => renderSportSkillChart(s))
                    ) : (
                        <Grid columns={2} stackable>
                            {userRatingData.allSportStats.map(s => (
                                <Grid.Column key={s.sportId}>
                                    {renderSportSkillChart(s)}
                                </Grid.Column>
                            ))}
                        </Grid>
                    )}
                </div>
            ) : (
                <Message info>
                    <Message.Header>{t('userRatings.noSportStats')}</Message.Header>
                    <p>{t('userRatings.noStatsMessage')}</p>
                </Message>
            )}
        </div>
    );
});